import { flowRight } from 'lodash';
import * as React from 'react';
import bind from '@reverbdotcom/commons/dist/bind';
import * as elog from '@reverbdotcom/commons/dist/elog';
import { MutationFn } from 'react-apollo';
import {
  SubmitCheckoutChallengeShopper,
  SubmitCheckoutIdentifyShopper,
  core_apimessages_AdyenPaymentResult_PaymentStatus as PaymentStatus,
  core_apimessages_CartItemAttributes as CartItemAttributes,
  core_apimessages_Error,
} from '../../schemas/rql';
import Location from '../../lib/wrapped_location';
import {
  connectCheckoutChallengeShopper,
  connectCheckoutIdentifyShopper,
  connectGuestCheckoutIdentifyShopper,
  connectGuestCheckoutChallengeShopper,
} from './threeds2_checkout_operations';
import {
  submitCheckoutChallengeShopper,
  submitCheckoutIdentifyShopper,
  submitGuestCheckoutChallengeShopper,
  submitGuestCheckoutIdentifyShopper,
} from './threeds2_checkout_submission';
import { PaymentStatusCallbacks } from './payment_status_callbacks';
import ThreeDS2Interaction from './threeds2_interaction';
import { withUserContext, IUserContext } from '@reverbdotcom/commons/dist/components/user_context_provider';

interface ApolloProps {
  checkoutIdentifyShopper: MutationFn<SubmitCheckoutIdentifyShopper.Mutation, SubmitCheckoutIdentifyShopper.Variables>;
  checkoutChallengeShopper: MutationFn<SubmitCheckoutChallengeShopper.Mutation, SubmitCheckoutChallengeShopper.Variables>;
  guestCheckoutIdentifyShopper: MutationFn<SubmitCheckoutIdentifyShopper.Mutation, SubmitCheckoutIdentifyShopper.Variables>;
  guestCheckoutChallengeShopper: MutationFn<SubmitCheckoutChallengeShopper.Mutation, SubmitCheckoutChallengeShopper.Variables>;
}

interface IExternalProps {
  cartItemAttributes?: CartItemAttributes[];
  challengeToken: string;
  checkoutID: string;
  checkoutUUID: string;
  currency: string;
  fingerprintToken: string;
  paymentData: string;
  paymentStatus: PaymentStatus;
  totalAmountCents: number;
  redirectOnFailureUrl: string;
  channelBrandingToken: string;
}

export type Props = IExternalProps & ApolloProps & IUserContext;

export interface IState {
  paymentStatus: PaymentStatus;
  fingerprintToken: string;
  challengeToken: string;
}

const TAG = 'adyen.threeds2_checkout_container';

export class ThreeDS2CheckoutContainer extends React.Component<Props, IState> {
  state = {
    fingerprintToken: this.props.fingerprintToken,
    challengeToken: this.props.challengeToken,
    paymentStatus: this.props.paymentStatus,
  };

  get totalPrice() {
    return {
      amountCents: this.props.totalAmountCents,
      currency: this.props.currency,
    };
  }

  get resultCallbacks(): PaymentStatusCallbacks {
    return {
      onSuccess: this.onSuccess,
      onChallengeShopperRequired: this.onChallengeShopperRequired,
      onError: this.onThreeDSFailed,
    };
  }

  eventData(additionalContext = {}) {
    return {
      componentName: 'ThreeDS2CheckoutContainer',
      context: {
        props: { ...this.props },
        state: { ...this.state },
        ...additionalContext,
      },
    };
  }

  componentDidMount() {
    elog.info(`${TAG}.mount`, { ...this.eventData() });
  }

  @bind
  onChallengeShopperRequired(challengeToken: string) {
    this.setState({
      challengeToken,
      paymentStatus: PaymentStatus.CHALLENGE_SHOPPER_REQUIRED,
    });
  }

  @bind
  onIdentifyShopperComplete(fingerprintResult: string) {
    submitCheckoutIdentifyShopper(
      {
        checkoutUuid: this.props.checkoutUUID,
        checkoutId: this.props.checkoutID,
        totalPrice: this.totalPrice,
        results: {
          threeds2FingerprintResult: fingerprintResult,
          paymentData: this.props.paymentData,
        },
        channelBrandingToken: this.props.channelBrandingToken,
      },
      this.props.checkoutIdentifyShopper,
      this.resultCallbacks,
    );
  }

  @bind
  onChallengeShopperComplete(challengeResult: string) {
    submitCheckoutChallengeShopper(
      {
        checkoutUuid: this.props.checkoutUUID,
        checkoutId: this.props.checkoutID,
        totalPrice: this.totalPrice,
        results: {
          threeds2ChallengeResult: challengeResult,
          paymentData: this.props.paymentData,
        },
        channelBrandingToken: this.props.channelBrandingToken,
      },
      this.props.checkoutChallengeShopper,
      this.resultCallbacks,
    );
  }

  @bind
  onGuestCheckoutIdentifyShopper(fingerprintResult: string) {
    submitGuestCheckoutIdentifyShopper(
      {
        checkoutUuid: this.props.checkoutUUID,
        checkoutId: this.props.checkoutID,
        totalPrice: this.totalPrice,
        results: {
          threeds2FingerprintResult: fingerprintResult,
          paymentData: this.props.paymentData,
        },
        channelBrandingToken: this.props.channelBrandingToken,
        cartItemAttributes: this.props.cartItemAttributes,
      },
      this.props.guestCheckoutIdentifyShopper,
      this.resultCallbacks,
    );
  }

  @bind
  onGuestChallengeShopperComplete(challengeResult: string) {
    submitGuestCheckoutChallengeShopper(
      {
        checkoutUuid: this.props.checkoutUUID,
        checkoutId: this.props.checkoutID,
        totalPrice: this.totalPrice,
        results: {
          threeds2ChallengeResult: challengeResult,
          paymentData: this.props.paymentData,
        },
        channelBrandingToken: this.props.channelBrandingToken,
        cartItemAttributes: this.props.cartItemAttributes,
      },
      this.props.guestCheckoutChallengeShopper,
      this.resultCallbacks,
    );
  }

  @bind
  onSuccess(paymentResultUrl: string) {
    elog.info(`${TAG}.success`, {
      checkoutID: this.props.checkoutID,
      checkoutUUID: this.props.checkoutUUID,
      paymentStatus: this.props.paymentStatus,
    });
    Location.assign(paymentResultUrl);
  }

  @bind
  onThreeDSFailed(errors: core_apimessages_Error[]) {
    elog.info(`${TAG}.failed`, {
      errors,
      checkoutUuid: this.props.checkoutUUID,
      orderBundleId: this.props.checkoutID,
      paymentStatus: this.state.paymentStatus,
    });
    Location.assign(this.props.redirectOnFailureUrl);
  }

  render() {
    if (this.props.user.loggedOut) {
      return (
        <ThreeDS2Interaction
          challengeToken={this.state.challengeToken}
          fingerprintToken={this.state.fingerprintToken}
          onChallengeShopperComplete={this.onGuestChallengeShopperComplete}
          onIdentifyShopperComplete={this.onGuestCheckoutIdentifyShopper}
          paymentStatus={this.state.paymentStatus}
        />
      );
    }

    return (
      <ThreeDS2Interaction
        challengeToken={this.state.challengeToken}
        fingerprintToken={this.state.fingerprintToken}
        onChallengeShopperComplete={this.onChallengeShopperComplete}
        onIdentifyShopperComplete={this.onIdentifyShopperComplete}
        paymentStatus={this.state.paymentStatus}
      />
    );
  }
}

export default flowRight([
  connectCheckoutIdentifyShopper,
  connectCheckoutChallengeShopper,
  connectGuestCheckoutIdentifyShopper,
  connectGuestCheckoutChallengeShopper,
])(withUserContext(ThreeDS2CheckoutContainer));



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/discovery-ui/src/credit_cards/threeds2/threeds2_checkout_container.tsx